import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type ThemePreference = 'light' | 'dark' | 'system';

const THEME_STORAGE_KEY = 'user_theme_preference';

@Injectable({
	providedIn: 'root',
})
export class ThemeService {
	private _preference: ThemePreference = 'system';
	private _isDarkMode = new BehaviorSubject<boolean>(false);
	
	/** Observable that emits the current dark mode state */
	public isDarkMode$ = this._isDarkMode.asObservable();
	
	/** Current dark mode state */
	public get isDarkMode(): boolean {
		return this._isDarkMode.value;
	}
	
	/** Current theme preference */
	public get preference(): ThemePreference {
		return this._preference;
	}

	constructor() {
		this.init();
	}

	/**
	 * Initialize the theme service - load saved preference and apply it
	 */
	private init(): void {
		// Load saved preference from localStorage
		const saved = localStorage.getItem(THEME_STORAGE_KEY) as ThemePreference | null;
		if (saved && ['light', 'dark', 'system'].includes(saved)) {
			this._preference = saved;
		} else {
			this._preference = 'system';
		}

		// Apply the theme
		this.applyTheme();

		// Listen for system preference changes
		const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
		mediaQuery.addEventListener('change', () => {
			if (this._preference === 'system') {
				this.applyTheme();
			}
		});
	}

	/**
	 * Toggle between light and dark mode
	 * If currently on 'system', switches to the opposite of current appearance
	 */
	public toggle(): void {
		if (this._preference === 'system') {
			// Currently system - switch to opposite of current appearance
			const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
			this._preference = systemDark ? 'light' : 'dark';
		} else if (this._preference === 'dark') {
			this._preference = 'light';
		} else {
			this._preference = 'dark';
		}

		this.savePreference();
		this.applyTheme();
	}

	/**
	 * Set a specific theme preference
	 */
	public setPreference(preference: ThemePreference): void {
		this._preference = preference;
		this.savePreference();
		this.applyTheme();
	}

	/**
	 * Clear the saved preference and revert to system
	 * Call this on logout
	 */
	public clearPreference(): void {
		this._preference = 'system';
		localStorage.removeItem(THEME_STORAGE_KEY);
		this.applyTheme();
	}

	/**
	 * Apply the current theme to the document
	 */
	private applyTheme(): void {
		const body = document.body;
		let isDark: boolean;

		if (this._preference === 'system') {
			// Follow system preference - remove override classes
			body.classList.remove('dark', 'light');
			isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
		} else if (this._preference === 'dark') {
			body.classList.add('dark');
			body.classList.remove('light');
			isDark = true;
		} else {
			body.classList.add('light');
			body.classList.remove('dark');
			isDark = false;
		}

		this._isDarkMode.next(isDark);
	}

	/**
	 * Save the current preference to localStorage
	 */
	private savePreference(): void {
		localStorage.setItem(THEME_STORAGE_KEY, this._preference);
	}
}
